import styles from "styles/pages/reports/reports.module.scss";

import { Currency } from "shared/utils/Currency";
import { useEffect, useRef, useState } from "react";

import classNames from "classnames/bind";
import { useNavigate } from "react-router";
import { useDragScroll } from "shared/hooks/useDragScroll";
import { If } from "shared/components/control-statements";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { Numbers } from "shared/utils/Numbers";
import { formatNumberWithOneDecimal } from "pages/Reports/Reports";
const css = classNames.bind(styles);

const campaignStatus = {
  DRAFT: "Rascunho",
  PAUSED: "Em pausa",
  ARCHIVED: "Arquivado",
  PUBLISHED: "Publicado",
};

const creativeStatus = {
  DRAFT: "Rascunho",
  PAUSED: "Em pausa",
  ARCHIVED: "Arquivado",
  PUBLISHED: "Publicado",
};

const creativeTypes = {
  EmailCreativeAction: "E-mail",
  PushCreativeAction: "Push",
  SmsCreativeAction: "SMS",
  WhatsAppCreativeAction: "WhatsApp",
};

export default function CampaignDetails({
  data,
  isVisible,
  columns = [],
  columnsObject,
  hiddenColumns,
  filters,
  search = "",
}) {
  const [expandedCampaigns, setExpandedCampaigns] = useState([]);
  const [expandedCreatives, setExpandedCreatives] = useState([]);
  const [left, setLeft] = useState(0);

  useEffect(() => {
    if (expandedCampaigns.length > 0) {
      sessionStorage.setItem(
        "expandedCampaigns",
        JSON.stringify(expandedCampaigns)
      );
    }
  }, [expandedCampaigns]);

  useEffect(() => {
    if (expandedCreatives.length > 0) {
      sessionStorage.setItem(
        "expandedCreatives",
        JSON.stringify(expandedCreatives)
      );
    }
  }, [expandedCreatives]);

  useEffect(() => {
    const expandedCampaigns = sessionStorage.getItem("expandedCampaigns");
    const expandedCreatives = sessionStorage.getItem("expandedCreatives");
    if (expandedCampaigns) setExpandedCampaigns(JSON.parse(expandedCampaigns));
    if (expandedCreatives) setExpandedCreatives(JSON.parse(expandedCreatives));
  }, []);

  const toggleCampaign = (id) => {
    setExpandedCampaigns((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleCreative = (id) => {
    setExpandedCreatives((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const navigate = useNavigate();

  const { scrollRef, onMouseDown, wasDragging } = useDragScroll();

  const idColumnRef = useRef(null);
  const floatingElementRef = useRef(null);

  useEffect(() => {
    const el = idColumnRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        setLeft(width + 160 + 50 + 58);
      }
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [data]);

  function sumField(data, field, oneDecimal = false) {
    const sum = (data || []).reduce((sum, item) => {
      let value = Number(item[field]) || 0;
      if (field === "influenced" && value < 0) value = 0;
      return sum + value;
    }, 0);

    return parseFloat((sum || 0).toFixed(oneDecimal ? 1 : 2));
  }

  const september2025 = dayjs("2025-09-01T00:00:00.000Z");

  const rangeBeforeSeptember =
    dayjs(filters.start_date).isBefore(september2025) ||
    dayjs(filters.end_date).isBefore(september2025);

  const isBeforeSeptember = rangeBeforeSeptember;

  function calculateTotalROI(data) {
    if (!Array.isArray(data) || data.length === 0) return 0;

    const totals = data.reduce(
      (acc, item) => {
        acc.total_revenue += Number(item.total_revenue) || 0;
        acc.coupon_discounts += Number(item.coupon_discounts) || 0;
        acc.cost += Number(item.cost) || 0;
        return acc;
      },
      { total_revenue: 0, coupon_discounts: 0, cost: 0 }
    );

    const investment = totals.coupon_discounts + totals.cost;
    if (investment === 0) return 0;

    const roi = (totals.total_revenue - investment) / investment;
    return roi;
  }

  function calculateCouponROI(data) {
    if (!Array.isArray(data) || data.length === 0) return 0;

    const totals = data.reduce(
      (acc, item) => {
        acc.total_with_discounts += Number(item.total_with_discounts) || 0;
        acc.coupon_discounts += Number(item.coupon_discounts) || 0;
        acc.cost += Number(item.cost) || 0;
        return acc;
      },
      { total_with_discounts: 0, coupon_discounts: 0, cost: 0 }
    );

    const investment = totals.coupon_discounts + totals.cost;
    if (investment === 0) return 0;

    const roi = (totals.total_with_discounts - investment) / investment;
    return roi;
  }

  return (
    <div className={styles.campaignDetails}>
      <If cond={(data || []).length === 0}>
        <p className={styles.noData}>Nenhum dado foi encontrado</p>
      </If>
      <If cond={(data || []).length > 0}>
        <div className={styles.box2}>
          <div className={styles.container}>
            <div
              onMouseDown={onMouseDown}
              ref={scrollRef}
              className={styles.tableCampaignDetails}
              style={{
                maxHeight: isBeforeSeptember
                  ? "calc(100vh - 155px)"
                  : "calc(100vh - 95px)",
              }}
            >
              <table>
                <thead>
                  <tr>
                    <th
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 200,
                        backgroundColor: "#1F1F1F",
                        minWidth: 50,
                      }}
                    />
                    <th
                      style={{
                        position: "sticky",
                        left: 77,
                        zIndex: 200,
                        backgroundColor: "#1F1F1F",
                        width: 160,
                        maxWidth: 160,
                      }}
                    >
                      TIPO
                    </th>
                    <th
                      ref={idColumnRef}
                      style={{
                        position: "sticky",
                        left: 237,
                        zIndex: 200,
                        minWidth: 60,
                        backgroundColor: "#1F1F1F",
                        borderLeft: "1px solid rgba(51, 51, 51, .55)",
                      }}
                    >
                      ID
                    </th>
                    <th
                      ref={floatingElementRef}
                      style={{
                        position: "sticky",
                        background: "rgba(51, 51, 51, 0.55)",
                        left: `${left}px`,
                        zIndex: 200,
                        minWidth: 1,
                        padding: 0,
                      }}
                    ></th>
                    {columns
                      .filter((column) => !hiddenColumns.includes(column))
                      .map((column) => {
                        return (
                          <th
                            style={{ minWidth: column === "NOME" ? 500 : "" }}
                            key={column}
                          >
                            {columnsObject[column].name}
                          </th>
                        );
                      })}
                  </tr>
                </thead>

                {(data || []).map((campaign, index) => {
                  const hasCoupons =
                    campaign.creatives.map((c) => c.coupons).flat().length > 0;

                  return (
                    <>
                      <tr style={{ height: 2.5 }}>
                        <td
                          colSpan={columns.length + 4 - hiddenColumns.length}
                          style={{
                            ...(expandedCampaigns.includes(campaign.id)
                              ? {
                                  borderBottom:
                                    "1px solid rgba(109, 109, 242, .5)",
                                }
                              : {
                                  borderBottom: "1px solid transparent",
                                }),
                          }}
                        ></td>
                      </tr>
                      <tbody
                        className={css({
                          collapsed: expandedCampaigns.includes(campaign.id),
                        })}
                      >
                        <tr
                          key={index}
                          className={css({
                            collapsed: expandedCampaigns.includes(campaign.id),
                          })}
                        >
                          <td
                            onClick={(e) => {
                              toggleCampaign(campaign.id);
                              e.stopPropagation();
                            }}
                            style={{
                              position: "sticky",
                              background: expandedCampaigns.includes(
                                campaign.id
                              )
                                ? "#1F1F1F"
                                : "#1c1c1c",
                              left: 0,
                              zIndex: 200,
                              minWidth: 50,
                              paddingLeft: 45,
                              cursor: "pointer",
                            }}
                          >
                            <div
                              className={css({
                                chevron: true,
                                collapsed: expandedCampaigns.includes(
                                  campaign.id
                                ),
                              })}
                            >
                              <i className="fas fa-chevron-right" />
                            </div>
                          </td>
                          <td
                            style={{
                              position: "sticky",
                              background: expandedCampaigns.includes(
                                campaign.id
                              )
                                ? "#1F1F1F"
                                : "#1c1c1c",
                              left: 77,
                              zIndex: 200,
                              width: 160,
                              maxWidth: 160,
                            }}
                          >
                            <div
                              className="d-flex align-center"
                              style={{ minWidth: 150, cursor: "pointer" }}
                              onClick={() => {
                                if (wasDragging()) return;
                                navigate(`/relatorios/campanha/${campaign.id}`);
                              }}
                            >
                              <p className={styles.campaign}>Campanha</p>
                            </div>
                          </td>
                          <td
                            style={{
                              position: "sticky",
                              background: expandedCampaigns.includes(
                                campaign.id
                              )
                                ? "#1F1F1F"
                                : "#1c1c1c",
                              left: 237,
                              zIndex: 200,
                              minWidth: 60,
                              borderLeft: "1px solid rgba(51, 51, 51, .55)",
                            }}
                          >
                            <strong>{campaign.id}</strong>
                          </td>
                          <td
                            style={{
                              position: "sticky",
                              background: "rgba(51, 51, 51, 0.55)",
                              left: `${left}px`,
                              zIndex: 200,
                              minWidth: 1,
                              padding: 0,
                            }}
                          />
                          <If cond={isVisible("NOME")}>
                            <td>{campaign.name}</td>
                          </If>
                          <If cond={isVisible("STATUS")}>
                            <td>{campaignStatus[campaign.state]}</td>
                          </If>
                          <If cond={isVisible("DATA DE CRIAÇÃO")}>
                            <td>
                              {dayjs(campaign.created_at).format("DD/MM/YYYY")}
                            </td>
                          </If>
                          <If cond={isVisible("ÚLTIMO DISPARO")}>
                            <td>
                              {campaign.last_send_at
                                ? dayjs(campaign.last_send_at).format(
                                    "DD/MM/YYYY"
                                  )
                                : "Sem disparos"}
                            </td>
                          </If>
                          <If cond={isVisible("CANAL")}>
                            <td>---</td>
                          </If>
                          <If cond={isVisible("FATURAMENTO INFLUENCIADO")}>
                            <td>
                              {Currency.formatCurrency(
                                campaign?.influenced < 0
                                  ? 0
                                  : campaign?.influenced
                              )}
                            </td>
                          </If>
                          <If cond={isVisible("FATURAMENTO COM CUPOM")}>
                            <td>
                              {Currency.formatCurrency(
                                campaign.total_with_discounts
                              )}
                            </td>
                          </If>
                          <If cond={isVisible("FATURAMENTO TOTAL")}>
                            <td>
                              <b>
                                {isBeforeSeptember
                                  ? "---"
                                  : Currency.formatCurrency(
                                      campaign.total_revenue
                                    )}
                              </b>
                            </td>
                          </If>
                          <If cond={isVisible("CUSTO DO DISPARO")}>
                            <td>{Currency.formatCurrency(campaign.cost)}</td>
                          </If>
                          <If cond={isVisible("DESCONTOS COM CUPOM")}>
                            <td>
                              {isBeforeSeptember
                                ? "---"
                                : Currency.formatCurrency(
                                    campaign.coupon_discounts
                                  )}
                            </td>
                          </If>
                          <If cond={isVisible("CUSTO TOTAL")}>
                            <td>
                              <b>
                                {isBeforeSeptember
                                  ? "---"
                                  : Currency.formatCurrency(
                                      campaign.total_cost
                                    )}
                              </b>
                            </td>
                          </If>
                          <If cond={isVisible("ROI CONSERVADOR")}>
                            <td>
                              {isBeforeSeptember || !hasCoupons
                                ? "---"
                                : formatNumberWithOneDecimal(
                                    campaign.roi_with_discounts
                                  )}
                            </td>
                          </If>
                          <If cond={isVisible("ROI TOTAL")}>
                            <td>
                              <b>
                                {isBeforeSeptember
                                  ? "---"
                                  : formatNumberWithOneDecimal(
                                      campaign.general_roi
                                    )}
                              </b>
                            </td>
                          </If>
                          <If cond={isVisible("CUPONS DISTRIBUÍDOS")}>
                            <td>{Numbers.formatInt(campaign.coupon_added)}</td>
                          </If>
                          <If cond={isVisible("QTD. DISPAROS")}>
                            <td>{Numbers.formatInt(campaign.sends)}</td>
                          </If>
                          <If cond={isVisible("QTD. ENTREGAS")}>
                            <td>Em breve</td>
                          </If>
                          <If cond={isVisible("QTD. ABERTURAS")}>
                            <td>{Numbers.formatInt(campaign.opens)}</td>
                          </If>
                          <If cond={isVisible("QTD. CLIQUES")}>
                            <td>{Numbers.formatInt(campaign.clicks)}</td>
                          </If>
                          <If cond={isVisible("QTD. USOS")}>
                            <td>
                              <b>{Numbers.formatInt(campaign.coupon_uses)}</b>
                            </td>
                          </If>
                        </tr>

                        {expandedCampaigns.includes(campaign.id) &&
                          campaign.creatives.map((creative) => {
                            return (
                              <>
                                <tr
                                  key={index}
                                  className={css({
                                    collapsed: expandedCampaigns.includes(
                                      campaign.id
                                    ),
                                  })}
                                >
                                  <td
                                    onClick={(e) => {
                                      toggleCreative(creative.id);
                                      e.stopPropagation();
                                    }}
                                    style={{
                                      position: "sticky",
                                      background: expandedCampaigns.includes(
                                        campaign.id
                                      )
                                        ? "#1F1F1F"
                                        : "#1c1c1c",
                                      left: 0,
                                      zIndex: 200,
                                      minWidth: 50,
                                      paddingLeft: 45,
                                      cursor: "pointer",
                                    }}
                                  >
                                    <If cond={creative.coupons.length}>
                                      <div
                                        className={css({
                                          chevron: true,
                                          collapsed: expandedCampaigns.includes(
                                            campaign.id
                                          ),
                                        })}
                                      >
                                        <i className="fas fa-chevron-right" />
                                      </div>
                                    </If>
                                  </td>
                                  <td
                                    style={{
                                      position: "sticky",
                                      background: expandedCampaigns.includes(
                                        campaign.id
                                      )
                                        ? "#1F1F1F"
                                        : "#1c1c1c",
                                      left: 77,
                                      zIndex: 200,
                                      width: 160,
                                      maxWidth: 160,
                                      cursor: "pointer",
                                    }}
                                    onClick={() => {
                                      if (wasDragging()) return;
                                      navigate(
                                        `/relatorios/criativo/${creative.id}?campanha=${campaign.id}`
                                      );
                                    }}
                                  >
                                    <div
                                      className="d-flex align-center"
                                      style={{ minWidth: 150 }}
                                    >
                                      <p className={styles.creative}>
                                        Criativo
                                      </p>
                                    </div>
                                  </td>
                                  <td
                                    style={{
                                      position: "sticky",
                                      background: expandedCampaigns.includes(
                                        campaign.id
                                      )
                                        ? "#1F1F1F"
                                        : "#1c1c1c",
                                      left: 237,
                                      zIndex: 200,
                                      minWidth: 60,
                                      borderLeft:
                                        "1px solid rgba(51, 51, 51, .55)",
                                      cursor: "pointer",
                                    }}
                                    onClick={(e) => {
                                      toast.success("Código copiado.", {
                                        position: "bottom-right",
                                        hideProgressBar: true,
                                        autoClose: 3000,
                                        closeButton: false,
                                        theme: "dark",
                                        style: {
                                          background: "#242424",
                                          color: "#fff",
                                          fontSize: 12,
                                          padding: "0 30px",
                                          maxHeight: "20px",
                                          boxShadow: "none",
                                        },
                                      });
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(
                                        creative.id
                                      );
                                    }}
                                  >
                                    <strong title={creative.id}>
                                      {creative.id.slice(0, 8)}{" "}
                                      <i
                                        className="far fa-copy"
                                        style={{
                                          color: "#4d4d4d",
                                          marginLeft: 10,
                                        }}
                                      />
                                    </strong>
                                  </td>
                                  <td
                                    style={{
                                      position: "sticky",
                                      background: "rgba(51, 51, 51, 0.55)",
                                      left: `${left}px`,
                                      zIndex: 200,
                                      minWidth: 1,
                                      padding: 0,
                                    }}
                                  />
                                  <If cond={isVisible("NOME")}>
                                    <td>{creative.name}</td>
                                  </If>
                                  <If cond={isVisible("STATUS")}>
                                    <td>{creativeStatus[campaign.state]}</td>
                                  </If>
                                  <If cond={isVisible("DATA DE CRIAÇÃO")}>
                                    <td>
                                      {dayjs(campaign.created_at).format(
                                        "DD/MM/YYYY"
                                      )}
                                    </td>
                                  </If>
                                  <If cond={isVisible("ÚLTIMO DISPARO")}>
                                    <td>
                                      {campaign.last_send_at
                                        ? dayjs(campaign.last_send_at).format(
                                            "DD/MM/YYYY"
                                          )
                                        : "Sem disparos"}
                                    </td>
                                  </If>
                                  <If cond={isVisible("CANAL")}>
                                    <td>{creativeTypes[creative.type]}</td>
                                  </If>
                                  <If
                                    cond={isVisible("FATURAMENTO INFLUENCIADO")}
                                  >
                                    <td>---</td>
                                  </If>
                                  <If cond={isVisible("FATURAMENTO COM CUPOM")}>
                                    <td>---</td>
                                  </If>
                                  <If cond={isVisible("FATURAMENTO TOTAL")}>
                                    <td>
                                      <b>---</b>
                                    </td>
                                  </If>
                                  <If cond={isVisible("CUSTO DO DISPARO")}>
                                    <td>
                                      <If
                                        cond={
                                          creative.type === "PushCreativeAction"
                                        }
                                      >
                                        Sem custo
                                      </If>
                                      <If
                                        cond={
                                          creative.type !== "PushCreativeAction"
                                        }
                                      >
                                        {Currency.formatCurrency(creative.cost)}
                                      </If>
                                    </td>
                                  </If>
                                  <If cond={isVisible("DESCONTOS COM CUPOM")}>
                                    <td>---</td>
                                  </If>
                                  <If cond={isVisible("CUSTO TOTAL")}>
                                    <td>
                                      <b>
                                        {isBeforeSeptember
                                          ? "---"
                                          : Currency.formatCurrency(
                                              creative.total_cost
                                            )}
                                      </b>
                                    </td>
                                  </If>
                                  <If cond={isVisible("ROI CONSERVADOR")}>
                                    <td>
                                      {isBeforeSeptember || !hasCoupons
                                        ? "---"
                                        : formatNumberWithOneDecimal(
                                            campaign.roi_with_discounts
                                          )}
                                    </td>
                                  </If>
                                  <If cond={isVisible("ROI TOTAL")}>
                                    <td>
                                      <b>---</b>
                                    </td>
                                  </If>
                                  <If cond={isVisible("CUPONS DISTRIBUÍDOS")}>
                                    <td>---</td>
                                  </If>
                                  <If cond={isVisible("QTD. DISPAROS")}>
                                    <td>{Numbers.formatInt(creative.sends)}</td>
                                  </If>
                                  <If cond={isVisible("QTD. ENTREGAS")}>
                                    <td>Em breve</td>
                                  </If>
                                  <If cond={isVisible("QTD. ABERTURAS")}>
                                    <td>{Numbers.formatInt(creative.opens)}</td>
                                  </If>
                                  <If cond={isVisible("QTD. CLIQUES")}>
                                    <td>
                                      {Numbers.formatInt(creative.clicks)}
                                    </td>
                                  </If>
                                  <If cond={isVisible("QTD. USOS")}>
                                    <td>
                                      <b>---</b>
                                    </td>
                                  </If>
                                </tr>

                                {expandedCreatives.includes(creative.id) &&
                                  creative.coupons.slice(0, 1).map((coupon) => {
                                    return (
                                      <tr
                                        key={index}
                                        className={styles.collapsed}
                                      >
                                        <td
                                          style={{
                                            position: "sticky",
                                            background:
                                              expandedCampaigns.includes(
                                                campaign.id
                                              )
                                                ? "#1F1F1F"
                                                : "#1c1c1c",
                                            left: 0,
                                            zIndex: 200,
                                            minWidth: 50,
                                          }}
                                        />
                                        <td
                                          style={{
                                            position: "sticky",
                                            background:
                                              expandedCampaigns.includes(
                                                campaign.id
                                              )
                                                ? "#1F1F1F"
                                                : "#1c1c1c",
                                            left: 77,
                                            zIndex: 200,
                                            width: 160,
                                            maxWidth: 160,
                                            cursor: "pointer",
                                          }}
                                          onClick={() => {
                                            if (wasDragging()) return;
                                            navigate(
                                              `/relatorios/cupom/${coupon?.pins[0]?.coupon?.id}?campanha=${campaign.id}&criativo=${creative.id}`
                                            );
                                          }}
                                        >
                                          <div className="d-flex align-center">
                                            <p className={styles.coupon}>
                                              Cupom
                                            </p>
                                          </div>
                                        </td>
                                        <td
                                          style={{
                                            position: "sticky",
                                            background:
                                              expandedCampaigns.includes(
                                                campaign.id
                                              )
                                                ? "#1F1F1F"
                                                : "#1c1c1c",
                                            left: 237,
                                            zIndex: 200,
                                            minWidth: 60,
                                            borderLeft:
                                              "1px solid rgba(51, 51, 51, .55)",
                                          }}
                                        >
                                          <strong>
                                            {coupon.pins[0].coupon.id}
                                          </strong>
                                        </td>
                                        <td
                                          style={{
                                            position: "sticky",
                                            background:
                                              "rgba(51, 51, 51, 0.55)",
                                            left: `${left}px`,
                                            zIndex: 200,
                                            minWidth: 1,
                                            padding: 0,
                                          }}
                                        />
                                        <If cond={isVisible("NOME")}>
                                          <td>{coupon.code}</td>
                                        </If>
                                        <If cond={isVisible("STATUS")}>
                                          <td>---</td>
                                        </If>
                                        <If cond={isVisible("DATA DE CRIAÇÃO")}>
                                          <td>---</td>
                                        </If>
                                        <If cond={isVisible("ÚLTIMO DISPARO")}>
                                          <td>
                                            {campaign.last_send_at
                                              ? dayjs(
                                                  campaign.last_send_at
                                                ).format("DD/MM/YYYY")
                                              : "Sem disparos"}
                                          </td>
                                        </If>
                                        <If cond={isVisible("CANAL")}>
                                          <td>---</td>
                                        </If>
                                        <If
                                          cond={isVisible(
                                            "FATURAMENTO INFLUENCIADO"
                                          )}
                                        >
                                          <td>---</td>
                                        </If>
                                        <If
                                          cond={isVisible(
                                            "FATURAMENTO COM CUPOM"
                                          )}
                                        >
                                          <td>
                                            {Currency.formatCurrency(
                                              coupon.total
                                            )}
                                          </td>
                                        </If>
                                        <If
                                          cond={isVisible("FATURAMENTO TOTAL")}
                                        >
                                          <td>
                                            <b>---</b>
                                          </td>
                                        </If>
                                        <If
                                          cond={isVisible("CUSTO DO DISPARO")}
                                        >
                                          <td>---</td>
                                        </If>
                                        <If
                                          cond={isVisible(
                                            "DESCONTOS COM CUPOM"
                                          )}
                                        >
                                          <td>
                                            {isBeforeSeptember
                                              ? "---"
                                              : Currency.formatCurrency(
                                                  coupon.discounts
                                                )}
                                          </td>
                                        </If>
                                        <If cond={isVisible("CUSTO TOTAL")}>
                                          <td>
                                            <b>
                                              {isBeforeSeptember
                                                ? "---"
                                                : Currency.formatCurrency(
                                                    coupon.total_cost
                                                  )}
                                            </b>
                                          </td>
                                        </If>
                                        <If cond={isVisible("ROI CONSERVADOR")}>
                                          <td>---</td>
                                        </If>
                                        <If cond={isVisible("ROI TOTAL")}>
                                          <td>
                                            <b>---</b>
                                          </td>
                                        </If>
                                        <If
                                          cond={isVisible(
                                            "CUPONS DISTRIBUÍDOS"
                                          )}
                                        >
                                          <td>
                                            {Numbers.formatInt(
                                              campaign.coupon_added
                                            )}
                                          </td>
                                        </If>
                                        <If cond={isVisible("QTD. DISPAROS")}>
                                          <td>---</td>
                                        </If>
                                        <If cond={isVisible("QTD. ENTREGAS")}>
                                          <td>---</td>
                                        </If>
                                        <If cond={isVisible("QTD. ABERTURAS")}>
                                          <td>---</td>
                                        </If>
                                        <If cond={isVisible("QTD. CLIQUES")}>
                                          <td>---</td>
                                        </If>
                                        <If cond={isVisible("QTD. USOS")}>
                                          <td>
                                            <b>
                                              {Numbers.formatInt(coupon.uses)}
                                            </b>
                                          </td>
                                        </If>
                                      </tr>
                                    );
                                  })}
                              </>
                            );
                          })}
                      </tbody>
                      <tr style={{ height: 2.5 }}>
                        <td
                          colSpan={columns.length + 4 - hiddenColumns.length}
                          style={{
                            ...(expandedCampaigns.includes(campaign.id)
                              ? {
                                  borderBottom:
                                    "1px solid rgba(109, 109, 242, .5)",
                                }
                              : {
                                  borderBottom: "1px solid transparent",
                                }),
                          }}
                        ></td>
                      </tr>
                    </>
                  );
                })}
                <tfoot>
                  <tr>
                    <th
                      style={{
                        position: "sticky",
                        left: 0,
                        zIndex: 200,
                        backgroundColor: "#242424",
                        minWidth: 50,
                      }}
                    />
                    <th
                      style={{
                        position: "sticky",
                        left: 77,
                        zIndex: 200,
                        backgroundColor: "#242424",
                        width: 160,
                        maxWidth: 160,
                      }}
                    >
                      ---
                    </th>
                    <th
                      ref={idColumnRef}
                      style={{
                        position: "sticky",
                        left: 237,
                        zIndex: 200,
                        minWidth: 60,
                        backgroundColor: "#242424",
                        borderLeft: "1px solid rgba(51, 51, 51, .55)",
                      }}
                    >
                      ---
                    </th>
                    <th
                      ref={floatingElementRef}
                      style={{
                        position: "sticky",
                        background: "rgba(51, 51, 51, 0.55)",
                        left: `${left}px`,
                        zIndex: 200,
                        minWidth: 1,
                        padding: 0,
                      }}
                    ></th>
                    <If cond={isVisible("NOME")}>
                      <th>---</th>
                    </If>
                    <If cond={isVisible("STATUS")}>
                      <th>---</th>
                    </If>
                    <If cond={isVisible("DATA DE CRIAÇÃO")}>
                      <th>---</th>
                    </If>
                    <If cond={isVisible("ÚLTIMO DISPARO")}>
                      <th>---</th>
                    </If>
                    <If cond={isVisible("CANAL")}>
                      <th>---</th>
                    </If>
                    <If cond={isVisible("FATURAMENTO INFLUENCIADO")}>
                      <th>
                        {Currency.formatCurrency(sumField(data, "influenced"))}
                      </th>
                    </If>
                    <If cond={isVisible("FATURAMENTO COM CUPOM")}>
                      <th>
                        {Currency.formatCurrency(
                          sumField(data, "total_with_discounts")
                        )}
                      </th>
                    </If>
                    <If cond={isVisible("FATURAMENTO TOTAL")}>
                      <th>
                        <b>
                          <If cond={isBeforeSeptember}>---</If>
                          <If cond={!isBeforeSeptember}>
                            {Currency.formatCurrency(
                              sumField(data, "total_revenue")
                            )}
                          </If>
                        </b>
                      </th>
                    </If>
                    <If cond={isVisible("CUSTO DO DISPARO")}>
                      <th>{Currency.formatCurrency(sumField(data, "cost"))}</th>
                    </If>
                    <If cond={isVisible("DESCONTOS COM CUPOM")}>
                      <th>
                        <If cond={isBeforeSeptember}>---</If>
                        <If cond={!isBeforeSeptember}>
                          {Currency.formatCurrency(
                            sumField(data, "coupon_discounts")
                          )}
                        </If>
                      </th>
                    </If>
                    <If cond={isVisible("CUSTO TOTAL")}>
                      <th>
                        <b>
                          {isBeforeSeptember
                            ? "---"
                            : Currency.formatCurrency(
                                sumField(data, "total_cost")
                              )}
                        </b>
                      </th>
                    </If>
                    <If cond={isVisible("ROI CONSERVADOR")}>
                      <th>
                        <If cond={isBeforeSeptember}>---</If>
                        <If cond={!isBeforeSeptember}>
                          {formatNumberWithOneDecimal(calculateCouponROI(data))}
                        </If>
                      </th>
                    </If>
                    <If cond={isVisible("ROI TOTAL")}>
                      <th>
                        <b>
                          <If cond={isBeforeSeptember}>---</If>
                          <If cond={!isBeforeSeptember}>
                            {formatNumberWithOneDecimal(
                              calculateTotalROI(data)
                            )}
                          </If>
                        </b>
                      </th>
                    </If>
                    <If cond={isVisible("CUPONS DISTRIBUÍDOS")}>
                      <th>
                        {Numbers.formatInt(sumField(data, "coupon_added"))}
                      </th>
                    </If>
                    <If cond={isVisible("QTD. DISPAROS")}>
                      <th>{Numbers.formatInt(sumField(data, "sends"))}</th>
                    </If>
                    <If cond={isVisible("QTD. ENTREGAS")}>
                      <th>---</th>
                    </If>
                    <If cond={isVisible("QTD. ABERTURAS")}>
                      <th>{Numbers.formatInt(sumField(data, "opens"))}</th>
                    </If>
                    <If cond={isVisible("QTD. CLIQUES")}>
                      <th>{Numbers.formatInt(sumField(data, "clicks"))}</th>
                    </If>
                    <If cond={isVisible("QTD. USOS")}>
                      <th>
                        <b>
                          {Numbers.formatInt(sumField(data, "coupon_uses"))}
                        </b>
                      </th>
                    </If>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </If>
    </div>
  );
}
